import axiosWrapper from './axiosWrapper';
import { getProjects, getProject } from '../cache/dataClient';
import cacheSync from '../cache/cacheSync';

const projectService = {
  // Get all projects for the current user (offline-first)
  async getUserProjects(options = {}) {
    const { useCache = true, backgroundRefresh = false } = options;
    const result = await getProjects({ useCache, backgroundRefresh });
    if (result.error) {
      return { data: null, error: result.error };
    }

    // Return in format expected by existing code
    return {
      data: { projects: result.data || [] },
      error: null,
      fromCache: result.fromCache,
    };
  },

  // Get a specific project by ID (offline-first)
  async getProjectById(projectId, options = {}) {
    const { useCache = true, backgroundRefresh = false } = options;
    const result = await getProject(projectId, { useCache, backgroundRefresh });
    if (result.error) {
      return { data: null, error: result.error };
    }

    // Return in format expected by existing code
    return {
      data: { project: result.data },
      error: null,
      fromCache: result.fromCache,
    };
  },

  // Create a new project
  async createProject(projectData) {
    const response = await axiosWrapper({
      method: 'post',
      path: 'projects',
      data: projectData,
    });
    // Update cache after successful creation
    if (!response.error) {
      cacheSync.syncAfterMutation('projects', response, 'create').catch((err) => {
        console.warn('[projectService] Error updating cache after create:', err);
      });
    }
    return response;
  },

  // Update an existing project
  async updateProject(projectId, projectData) {
    const response = await axiosWrapper({
      method: 'put',
      path: `projects/${projectId}`,
      data: projectData,
    });
    // Update cache after successful update
    if (!response.error) {
      cacheSync.syncAfterMutation('projects', response, 'update').catch((err) => {
        console.warn('[projectService] Error updating cache after update:', err);
      });
    }
    return response;
  },

  // Delete a project (soft delete)
  async deleteProject(projectId) {
    const response = await axiosWrapper({
      method: 'delete',
      path: `projects/${projectId}`,
    });
    // Update cache after successful delete
    if (!response.error) {
      // For delete, we need to pass the projectId since response might not have the entity
      cacheSync.syncAfterMutation('projects', { id: projectId }, 'delete').catch((err) => {
        console.warn('[projectService] Error updating cache after delete:', err);
      });
    }
    return response;
  },

  // ---- Sharing ----

  async getCollaborators(projectId) {
    return axiosWrapper({ method: 'get', path: `projects/${projectId}/collaborators` });
  },

  /** Share with a user by email. role: 'viewer' | 'commenter' | 'editor'. */
  async addCollaborator(projectId, email, role) {
    return axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/collaborators`,
      data: { email, role },
    });
  },

  async updateCollaboratorRole(projectId, collaboratorUserId, role) {
    return axiosWrapper({
      method: 'put',
      path: `projects/${projectId}/collaborators/${collaboratorUserId}`,
      data: { role },
    });
  },

  async removeCollaborator(projectId, collaboratorUserId) {
    return axiosWrapper({
      method: 'delete',
      path: `projects/${projectId}/collaborators/${collaboratorUserId}`,
    });
  },

  /**
   * Thin index of every scene in a project (id, chapterId, name, description,
   * position, wordCount) in a single request — used by the command palette.
   */
  async getSceneIndex(projectId) {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/scene-index`,
    });
    return response;
  },

  async exportProjectDocx(projectId) {
    const response = await axiosWrapper({
      method: 'get',
      path: `projects/${projectId}/export-docx`,
      config: {
        responseType: 'blob',
      },
    });
    return response;
  },

  // Export with user-selected options and format (docx, markdown, html, txt)
  async exportProjectFile(projectId, options = {}) {
    const response = await axiosWrapper({
      method: 'post',
      path: `projects/${projectId}/export-file`,
      data: options,
      config: {
        responseType: 'blob',
      },
    });
    return response;
  },
};

export default projectService;
